import * as React from "react";
import { Paper } from "@mui/material";
import Api, { APIs } from "../../../../Api";

// Highcharts Gantt + modules
import Highcharts from "highcharts/highcharts-gantt";
import StockModule from "highcharts/modules/stock";
import Accessibility from "highcharts/modules/accessibility";
import Exporting from "highcharts/modules/exporting";
import TreeGrid from "highcharts/modules/treegrid";

// Initialize modules
StockModule(Highcharts);
Accessibility(Highcharts);
Exporting(Highcharts);
TreeGrid(Highcharts);

interface IProps {
    pId: number;
}

const ProjectGanttChart: React.FC<IProps> = ({ pId }) => {
    const containerId = "gantt-container";
    const chartRef = React.useRef<Highcharts.Chart | null>(null);

    const createGanttChart = (series: Highcharts.SeriesOptionsType[]) => {
        const options: Highcharts.Options = {
            chart: {
                scrollablePlotArea: {}
            },
            // TreeGrid augments YAxis with uniqueNames
            yAxis: {
                uniqueNames: false
            } as unknown as Highcharts.YAxisOptions, // safe cast for some TS setups

            xAxis: {
                currentDateIndicator: true
            },

            navigator: {
                enabled: true,
                series: {
                    type: "gantt",
                    pointPadding: 0.25,
                    accessibility: { enabled: false }
                },
                yAxis: {
                    reversed: true,
                    categories: []
                }
            },

            scrollbar: { enabled: true },

            rangeSelector: {
                enabled: true,
                selected: 0
            },

            accessibility: {
                point: {
                    descriptionFormatter: function (point: any) {
                        const completedValue = point.completed
                            ? point.completed.amount ?? point.completed
                            : null;
                        const completed = completedValue
                            ? ` Task ${Math.round(completedValue * 1000) / 10}% completed.`
                            : "";
                        return Highcharts.format(
                            "{point.yCategory}.{completed} Start {point.x:%Y-%m-%d}, end {point.x2:%Y-%m-%d}.",
                            { point, completed }
                        );
                    }
                },
                series: {
                    descriptionFormatter: function (series: any) {
                        return series.name;
                    }
                }
            },

            series
        };

        // Destroy previous chart (if any) to avoid leaks when re-rendering
        if (chartRef.current) {
            chartRef.current.destroy();
            chartRef.current = null;
        }

        chartRef.current = Highcharts.ganttChart(containerId, options);

        // Zoom to data extent
        const { dataMin, dataMax } = chartRef.current.xAxis[0].getExtremes();
        chartRef.current.xAxis[0].setExtremes(dataMin, dataMax);
    };

    React.useEffect(() => {
        let isMounted = true;

        const loadAndRender = async () => {
            if (pId === -1) return;

            const payload = await Api.get(APIs.MoPD_PROJECT_WBS, { pId });
            if (!payload || !isMounted) return;

            const data: any[] = [];

            payload.data.forEach((d: any) => {
                if (d.start && d.end) {
                    const s = new Date(d.start);
                    const e = new Date(d.end);

                    // Use correct month index and day-of-month
                    const startUTC = Date.UTC(s.getFullYear(), s.getMonth(), s.getDate());
                    const endUTC = Date.UTC(e.getFullYear(), e.getMonth(), e.getDate());

                    const nd: any = {
                        id: String(d.act_id),
                        name: d.name,
                        completed: d.completed,
                        owner: d.owner,
                        start: startUTC,
                        end: endUTC
                    };

                    data.push(nd);
                }
            });

            if (data.length > 0) {
                const series: Highcharts.SeriesOptionsType[] = [
                    { type: "gantt", name: payload["name"], data }
                ];
                createGanttChart(series);
            } else {
                const container = document.getElementById(containerId);
                if (container) {
                    container.style.color = "black";
                    container.style.fontWeight = "600";
                    container.style.textAlign = "center";
                    container.style.lineHeight = "200px";
                    container.style.margin = "auto";
                    container.innerHTML = "No Gantt Chart Available...";
                }
            }
        };

        loadAndRender();

        return () => {
            isMounted = false;
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, [pId]);

    return (
        <Paper elevation={6} sx={{ height: "100%", width: "100%" }}>
            <div id={containerId} style={{ height: "100%", width: "100%" }} />
        </Paper>
    );
};

export default ProjectGanttChart;
